import React, { FC } from 'react'
import styled from 'styled-components'
import Logo from '../../assets/StonyWhite.png'
import FB from '../../assets/FacebookIcon.png'
import IG from '../../assets/InstagramIcon.png'
import LI from '../../assets/linkedin.png'
import Background from '../../assets/blackBG.png'

const FooterContainer = styled.footer`
  background-image: url(${Background});
  background-size: cover;
  background-color: #101010;
  color: white;
  padding: 3.25rem 3rem 1.75rem;

  @media (max-width: 768px) {
    padding: 2.5rem 1.5rem 1.5rem;
  }
`

const Inner = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  display: grid;
  /* Brand block, then links in their own columns — the links used to run as a
     single tall stack, which is most of what made the footer so long. */
  grid-template-columns: minmax(260px, 1.4fr) repeat(2, minmax(140px, 0.8fr)) auto;
  gap: 2.5rem 3rem;
  align-items: start;

  @media (max-width: 940px) {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 1.75rem;
  }
`

const Brand = styled.div`
  img {
    width: 230px;
    max-width: 100%;
    display: block;
    margin-bottom: 0.85rem;
  }

  h2 {
    color: white;
    font-size: 1.4rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    margin: 0 0 1rem;
  }

  p {
    margin: 0 0 0.3rem;
    font-size: var(--fs-body-sm);
    color: rgba(255, 255, 255, 0.78);
  }

  a {
    color: rgba(255, 255, 255, 0.78);
    text-decoration: none;
    font-size: var(--fs-body-sm);

    &:hover {
      color: white;
    }
  }
`

const ColHeading = styled.h3`
  color: rgba(255, 255, 255, 0.55);
  font-size: var(--fs-label);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin: 0 0 0.9rem;
`

const Links = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;

  a {
    color: rgba(255, 255, 255, 0.85);
    text-decoration: none;
    font-size: var(--fs-body-sm);
    width: fit-content;
    transition: color 0.2s ease;

    &:hover {
      color: white;
    }
  }
`

const Social = styled.div`
  display: flex;
  align-items: center;
  gap: 1.1rem;

  a {
    display: flex;
    align-items: center;
    opacity: 0.9;
    transition: opacity 0.2s ease, transform 0.2s ease;

    &:hover {
      opacity: 1;
      transform: translateY(-2px);
    }
  }

  img {
    height: 30px;
    width: auto;
  }
`

const Bottom = styled.div`
  max-width: 1240px;
  margin: 2.25rem auto 0;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.14);
  font-size: var(--fs-label);
  color: rgba(255, 255, 255, 0.55);
`

const SubstackMark: FC<{ size: number }> = ({ size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="white"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.539 0H1.46v2.836h21.08V0z" />
  </svg>
)

const SYLLABUS =
  'https://docs.google.com/document/d/16Cvxv-A0cWn821JXLTZbJ_E6qkxtgUcfhFwr9spFxCI/edit?usp=sharing'

const Footer: FC = () => (
  <FooterContainer>
    <Inner>
      <Brand>
        <img src={Logo} alt="Stony Brook University" />
        <h2>Podcast Incubator</h2>
        <p>
          Kathleen Russo, <em>Director</em>
        </p>
        <p>
          <a href="tel:6318829881">631-882-9881</a>
        </p>
        <p>
          <a href="mailto:kathleen.russo@stonybrook.edu">
            kathleen.russo@stonybrook.edu
          </a>
        </p>
      </Brand>

      <div>
        <ColHeading>Program</ColHeading>
        <Links aria-label="Program links">
          <a href="/apply">FAQs</a>
          <a href={SYLLABUS} target="_blank" rel="noopener noreferrer">
            Syllabus
          </a>
          <a href="/guiding-principles">Guiding Principles</a>
          <a href="/faculty">Faculty</a>
        </Links>
      </div>

      <div>
        <ColHeading>Explore</ColHeading>
        <Links aria-label="Explore links">
          <a href="/shows">Our Shows</a>
          <a href="/guests">Guests</a>
          <a href="/resources">Resources</a>
          <a href="/#contact">Contact</a>
        </Links>
      </div>

      <div>
        <ColHeading>Follow</ColHeading>
        <Social>
          <a
            href="https://www.linkedin.com/company/sbupodcastincubator/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
          >
            <img src={LI} alt="" />
          </a>
          <a
            href="https://www.instagram.com/sbupodcastincubator/?hl=en"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            <img src={IG} alt="" />
          </a>
          <a
            href="https://www.facebook.com/sbupodcastincubator"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
          >
            <img src={FB} alt="" />
          </a>
          <a
            href="https://sbupodcastincubator.substack.com/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Substack"
          >
            <SubstackMark size={26} />
          </a>
        </Social>
      </div>
    </Inner>

    <Bottom>
      © {new Date().getFullYear()} Stony Brook University Podcast Incubator
    </Bottom>
  </FooterContainer>
)

export default Footer
